import { useState } from 'react'
import { Badge } from '@/components/ui/badge'
import type { Photo, ReferencePhoto } from '@/types/visit'

function formatTag(tag: string): string {
  return tag
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

function formatVisitDate(dateString: string): string {
  const normalized = dateString.includes('T') ? dateString : `${dateString}T00:00:00`
  return new Date(normalized).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

interface PhotoComparisonProps {
  currentPhotos: Photo[]
  referencePhotos: ReferencePhoto[]
}

export default function PhotoComparison({ currentPhotos, referencePhotos }: PhotoComparisonProps) {
  const [selectedCurrentIndex, setSelectedCurrentIndex] = useState(0)
  const [selectedReferenceIndex, setSelectedReferenceIndex] = useState(0)

  if (referencePhotos.length === 0) return null

  const sortedCurrent = [...currentPhotos].sort((a, b) => a.order - b.order)
  const currentPhoto = sortedCurrent[selectedCurrentIndex] ?? sortedCurrent[0]
  const referencePhoto = referencePhotos[selectedReferenceIndex] ?? referencePhotos[0]

  if (!currentPhoto || !referencePhoto) return null

  return (
    <div className="rounded-lg border bg-white p-6">
      <h2 className="mb-4 text-lg font-medium text-slate-900">Photo Comparison</h2>
      <div className="grid grid-cols-2 gap-4">
        {/* Current visit */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-slate-700">Current Visit</span>
            <Badge variant="secondary">{formatTag(currentPhoto.tag)}</Badge>
          </div>
          <div className="aspect-square overflow-hidden rounded-lg border bg-slate-100">
            <img
              src={currentPhoto.url}
              alt={`Current - ${formatTag(currentPhoto.tag)}`}
              className="h-full w-full object-cover"
            />
          </div>
          {sortedCurrent.length > 1 && (
            <div className="flex gap-2">
              {sortedCurrent.map((photo, index) => (
                <button
                  key={photo.id}
                  onClick={() => setSelectedCurrentIndex(index)}
                  aria-label={`Select ${formatTag(photo.tag)} photo`}
                  aria-pressed={index === selectedCurrentIndex}
                  className={`h-12 w-12 overflow-hidden rounded border focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:outline-hidden ${
                    index === selectedCurrentIndex ? 'ring-2 ring-primary-500' : ''
                  }`}
                >
                  <img
                    src={photo.thumbnail_url}
                    alt={formatTag(photo.tag)}
                    className="h-full w-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Reference visit */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-slate-700">
              Previous Visit ({formatVisitDate(referencePhoto.visit_date)})
            </span>
            <Badge variant="secondary">{formatTag(referencePhoto.tag)}</Badge>
          </div>
          <div className="aspect-square overflow-hidden rounded-lg border bg-slate-100">
            <img
              src={referencePhoto.url}
              alt={`Previous - ${formatTag(referencePhoto.tag)}`}
              className="h-full w-full object-cover"
            />
          </div>
          {referencePhotos.length > 1 && (
            <div className="flex gap-2">
              {referencePhotos.map((photo, index) => (
                <button
                  key={photo.id}
                  onClick={() => setSelectedReferenceIndex(index)}
                  aria-label={`Select ${formatTag(photo.tag)} photo`}
                  aria-pressed={index === selectedReferenceIndex}
                  className={`h-12 w-12 overflow-hidden rounded border focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:outline-hidden ${
                    index === selectedReferenceIndex ? 'ring-2 ring-primary-500' : ''
                  }`}
                >
                  <img
                    src={photo.thumbnail_url}
                    alt={formatTag(photo.tag)}
                    className="h-full w-full object-cover"
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
